import config from "@Config/index";
import axios from "axios";
import { logger } from "helpers/logger";
import urlJoin from "helpers/urljoin";

/**
 * Data layer for the "Recommended properties for you" rail: `budget_city_rail` and
 * `student_choice` for one region, interleaved and de-duplicated. It does not exclude cards the
 * "Based on your <city> search" rail shows — in a thin region that pushed this rail below its
 * floor.
 */

// Larger than `TARGET_COUNT` because the two sources overlap heavily; 10 each still yields 10
// unique even with complete overlap.
const PER_SOURCE_LIMIT = 10;
const TARGET_COUNT = 10;
export const MINIMUM_TO_SHOW = 5;

const BUDGET_CITY_RAIL_PATH = "api/v0/inventories/budget_city_rail";
const STUDENT_CHOICE_PATH = "api/v0/inventories/student_choice";

interface SourceParams {
  regionCanonicalName: string;
  userLanguage?: string;
  userCurrency?: string;
}

const fetchSource = async (
  path: string,
  { regionCanonicalName, userLanguage = "en", userCurrency = "" }: SourceParams,
): Promise<any[]> => {
  if (!regionCanonicalName) return [];

  try {
    const url = urlJoin(config.API_URL, path);
    const res: any = await axios.get(url, {
      params: {
        region_canonical_name: regionCanonicalName,
        limit: PER_SOURCE_LIMIT,
        // Computed fields the card's rating badge renders from — absent unless asked for.
        methods: "images_with_featured,reviews_count,reviews_rating",
        lang: userLanguage,
        ...(userCurrency ? { user_currency: userCurrency } : {}),
        use_java_api: true,
      },
      headers: { "X-Use-Java-Api": "true" },
    });
    // These routes answer `{ data: [ ...inventories ] }`, not the paginated `data.result`.
    const payload = res?.data?.data;
    return Array.isArray(payload) ? payload : payload?.result ?? [];
  } catch (err) {
    if (!axios.isCancel(err)) logger.log(`Error in ${path} ${err}`);
    return [];
  }
};

/**
 * Interleaves the two sources, dedup by inventory id, capped at `TARGET_COUNT`. A duplicate only
 * advances the source it came from, so the other source keeps its turn.
 */
export const mergeRecommendedSources = (budgetItems: any[] = [], studentItems: any[] = []) => {
  const sources = [budgetItems, studentItems];
  const cursors = [0, 0];
  const seen = new Set<any>();
  const merged: any[] = [];

  const takeNext = (s: number) => {
    while (cursors[s] < sources[s].length) {
      const item = sources[s][cursors[s]];
      cursors[s] += 1;
      if (item?.id !== undefined && !seen.has(item.id)) {
        seen.add(item.id);
        return item;
      }
    }
    return null;
  };

  while (merged.length < TARGET_COUNT) {
    const budgetPick = takeNext(0);
    if (budgetPick) merged.push(budgetPick);
    const studentPick = merged.length < TARGET_COUNT ? takeNext(1) : null;
    if (studentPick) merged.push(studentPick);
    if (!budgetPick && !studentPick) break;
  }

  return merged;
};

const getRecommendedForYou = async (params: SourceParams): Promise<any[]> => {
  const [budget, student] = await Promise.all([
    fetchSource(BUDGET_CITY_RAIL_PATH, params),
    fetchSource(STUDENT_CHOICE_PATH, params),
  ]);
  return mergeRecommendedSources(budget, student);
};

export default getRecommendedForYou;
